import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, BookOpen, Plus, Trash2, Edit, GraduationCap } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState({ total_students: 0, total_courses: 0 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    program: 'master',
    activation_start: '',
    activation_end: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`, { headers }),
        axios.get(`${API_URL}/api/users`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      program: 'master',
      activation_start: '',
      activation_end: ''
    });
    setEditingUser(null);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        program: user.program || 'master',
        activation_start: user.activation_start ? user.activation_start.split('T')[0] : '',
        activation_end: user.activation_end ? user.activation_end.split('T')[0] : ''
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const headers = getAuthHeaders();

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        program: formData.program,
        activation_start: formData.activation_start ? new Date(formData.activation_start).toISOString() : null,
        activation_end: formData.activation_end ? new Date(formData.activation_end).toISOString() : null
      };

      if (editingUser) {
        // Update existing user
        if (formData.password) {
          payload.password = formData.password;
        }
        await axios.put(`${API_URL}/api/users/${editingUser.id}`, payload, { headers });
        toast.success('User updated successfully');
      } else {
        // Create new user
        payload.password = formData.password;
        payload.role = 'student';
        await axios.post(`${API_URL}/api/users`, payload, { headers });
        toast.success('User created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Operation failed';
      toast.error(typeof message === 'string' ? message : 'Operation failed');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/users/${userId}`, { headers });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const isUserActive = (user) => {
    if (!user.activation_start || !user.activation_end) return true;
    const now = new Date();
    const start = new Date(user.activation_start);
    const end = new Date(user.activation_end);
    return now >= start && now <= end;
  };

  const studentUsers = useMemo(() => users.filter(u => u.role === 'student'), [users]);

  if (loading) {
    return (
      <div className="flex min-h-screen pt-14 lg:pt-0">
        <Sidebar />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background">
          <div className="animate-pulse">Loading admin dashboard...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen pt-14 lg:pt-0" data-testid="admin-dashboard">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 lg:p-8 bg-background overflow-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light text-primary mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Admin Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage users, courses, and review student assignments
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="border border-border shadow-none" data-testid="stats-students">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">Students</p>
                  <p className="text-3xl font-light text-primary mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {stats.total_students}
                  </p>
                </div>
                <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border shadow-none" data-testid="stats-courses">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground uppercase tracking-wide">Courses</p>
                  <p className="text-3xl font-light text-primary mt-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {stats.total_courses}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-muted">
            <TabsTrigger value="users" data-testid="tab-users">User Management</TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="border border-border shadow-none" data-testid="users-table">
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-medium" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  <Users className="w-5 h-5 inline mr-2" />
                  Students ({studentUsers.length})
                </CardTitle>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button onClick={() => handleOpenDialog()} data-testid="add-user-button">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Student
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        {editingUser ? 'Edit Student' : 'Add New Student'}
                      </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                          data-testid="user-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="user-email-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password">
                          {editingUser ? 'New Password (leave blank to keep)' : 'Password'}
                        </Label>
                        <Input
                          id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required={!editingUser}
                          data-testid="user-password-input"
                        />
                      </div>

                      {/* Activation Period */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Program</Label>
                          <Select
                            value={formData.program}
                            onValueChange={(value) => setFormData({ ...formData, program: value })}
                          >
                            <SelectTrigger data-testid="program-select">
                              <SelectValue placeholder="Select program" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="master">Master Program</SelectItem>
                              <SelectItem value="doctor">Doctoral Program</SelectItem>
                              <SelectItem value="mdppm">MDPPM Program</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Activation Start</Label>
                          <Input
                            type="date"
                            value={formData.activation_start}
                            onChange={(e) => setFormData({ ...formData, activation_start: e.target.value })}
                            data-testid="activation-start-input"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                          <Label>Activation End</Label>
                          <Input
                            type="date"
                            value={formData.activation_end}
                            onChange={(e) => setFormData({ ...formData, activation_end: e.target.value })}
                            data-testid="activation-end-input"
                          />
                      </div>

                      <div className="flex justify-end gap-3 pt-4">
                        <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit" data-testid="save-user-button">
                          {editingUser ? 'Update' : 'Create'} Student
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="min-w-[700px] px-4 sm:px-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Program</TableHead>
                      <TableHead className="font-semibold">Activation Period</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studentUsers.map((user, index) => (
                      <TableRow key={user.id} data-testid={`user-row-${index}`}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell className="text-muted-foreground">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={user.program === 'doctor' ? 'border-purple-500 text-purple-700' : user.program === 'mdppm' ? 'border-emerald-500 text-emerald-700' : 'border-blue-500 text-blue-700'}>
                            <GraduationCap className="w-3 h-3 mr-1" />
                            {user.program === 'doctor' ? 'Doctoral' : user.program === 'mdppm' ? 'MDPPM' : 'Master'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.activation_start && user.activation_end ? (
                            <span className="text-sm">
                              {formatDate(user.activation_start)} - {formatDate(user.activation_end)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">No limit</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {isUserActive(user) ? (
                            <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-red-100 text-red-700 hover:bg-red-100" title={
                              user.activation_start && new Date(user.activation_start) > new Date()
                                ? 'Starts on ' + formatDate(user.activation_start)
                                : 'Activation period ended'
                            }>Inactive</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenDialog(user)}
                            data-testid={`edit-user-${index}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-user-${index}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {studentUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No students yet. Click "Add Student" to create one.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
